import { useContext, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import useAxios from "../../../../utils/useAxios";
import { Tooltip } from "react-tooltip";
import { ExpandableSection } from "../../../skus/components/expandable-section";
import OptionFormatter from "../../../../components/OptionFormatter";
import {
  numericBooleanOptions,
  storeLastFiltersAndResults,
  yesOrNoOptions,
} from "../../../../helpers";
import { SearchItemNumberPersistentData, } from "../../../../types/persistent-page-filters";
import { useColorContext } from "../../../../context/ColorContext";
import { useDatacatalogContext } from "../../../../context/DatacatalogContext";
import { DebounceContext, DebounceContextType, } from "../../../../context/DebounceContext";
import { useDecorationTechContext } from "../../../../context/DecorationTechContext";
import { getDecorTechs, } from "../../../../helpers/decor-tech.helper";
import { formatGlobalProductIntoOption, getGlobalProduct, } from "../../../../helpers/global-product.helper";
import { getItemNumber, getItemNumberDisplayCode, getTcc } from "../../../../helpers/tcc.helper";
import { fetchDecorTech } from "../../../../hooks/useDecorTech";
// import useDesign, { fetchDesign } from "../../../../hooks/useDesign";
import useGlobalProduct, {
  fetchGlobalProduct,
  fetchGlobalProductWithGPCode,
  fetchGlobalProductWithoutGPCode,
} from "../../../../hooks/useGlobalProduct";
import { fetchItemNumberValid } from "../../../../hooks/useItemNumber";
import { Predicates } from "../../../../libraries/predicates/predicates";
import { formatDatacalogIntoOption, getDatacatalog, } from "../../../../helpers/datacatalog.helper";
import {
  formatMarketingMaterialIntoOption,
  getMarketingMaterials,
} from "../../../../helpers/marketing-material.helper";
import { useMarketingMaterialContext } from "context/MarketingMaterialContext";
import { useFlavorScentContext } from "context/FlavorScentContext";
import { useChannelContext } from "context/ChannelContext";
import { useLicensorContext } from "context/LicensorContext";
import { useItemNumberSearchesStore } from "context/ItemNumberSearchesContext";
import { isStateEqualToInitialState } from "states/item-number-searches/Reducer";
import { ItemNumberSearchesActions } from "states/item-number-searches";
import {
  buildDropdownFiltersBasedOnLocalStorage,
  buildItemNumberSearchFilterObject,
} from "helpers/item-number-searches.helpers";
import { useLocation } from "react-router-dom";
import { ItemNumberSearchesState } from "states/item-number-searches/data.type";
import { getLinePlanningStatusOptions } from "helpers/line-planning-status.helper";
import { useLinePlanningStatusConditionsContext } from "context/LinePlanningStatusConditionsContext";
import { LIFECYCLE_STATUS_LABEL, } from "constants/labels";
// import { useGetItemNumber } from "../../../mpn/hooks/useGetItemNumber";
import { itemNumberToOptions } from "../../../mpn/helpers/itemNumberToOptions";
import { formatPlugTypeIntoOption, getPlugTypes } from "../../../../helpers/plug-type.helper";
import { formatConsumerTypeIntoOption, getConsumerTypes } from "../../../../helpers/consumer-type.helper";
import { useConsumerTypeContext } from "../../../../context/ConsumerTypeContext";
import { usePlugTypeContext } from "../../../../context/PlugTypeContext";
import useTcc, { fetchTcc, fetchTccValid } from "../../../../hooks/useTcc";
import { genericOptionFormatter } from "../../../mpn/helpers/genericOptionFormater";

const SearchFilters = ({
  isSearchRequest,
  filterProducts,
  prefix,
}: {
  isSearchRequest: boolean;
  filterProducts: any;
  prefix:
    | SearchItemNumberPersistentData["prefix"]
}) => {
  const axios = useAxios();
  const { searchDebounce } = useContext<DebounceContextType>(DebounceContext);
  const { state, dispatch } = useItemNumberSearchesStore();
  const location = useLocation();

  const { datacatalog, isLoading: isDatacatalogLoading } =
    useDatacatalogContext();

  // const { data: globalProducts, isLoading: isGlobalProductsLoading } =
  //   useGlobalProduct({
  //     category: state.subCategory?.parent?.id ?? state.category?.id,
  //     sub_category: state.subCategory?.id,
  //     product_line: state.productLine?.id,
  //   });
  //
  // const { data: ItemNumber, isLoading: itemNumberIsLoading } =
  //   useGetItemNumber();

  const { data: tccs, isLoading: isTccsLoading } = useTcc({
    category: state.subCategory?.parent?.id ?? state.category?.id,
    sub_category: state.subCategory?.id,
    product_line: state.productLine?.id,
    global_product: state.newGlobalProduct?.id,
  });

  const { data: colorsFetched, isLoading: isColorsLoading } = useColorContext();
  const { data: decoTechs, isLoading: isDecoTechsLoading } =
    useDecorationTechContext();
  // const { data: designs, isLoading: isDesignsLoading } = useDesign({
  //   decorationTechniqueId: String(state.decoTech?.id ?? ""),
  // });
  const { data: marketingMaterials, isLoading: isMarketingMaterialsLoading } =
    useMarketingMaterialContext();
  const { data: flavorScents, isLoading: isFlavorScentsLoading } =
    useFlavorScentContext();
  const { data: channels, isLoading: isChannelsLoading } = useChannelContext();
  const { data: licensors, isLoading: isLicensorsLoading } =
    useLicensorContext();
  const { data: consumerTypes, isLoading: isConsumerTypesLoading } =
    useConsumerTypeContext();
  const { data: plugTypes, isLoading: isPlugTypesLoading } =
    usePlugTypeContext();    
  // const { data: licensedProperties, isLoading: isLicensedPropertiesLoading } =
  //   useLicensedProperty({
  //     licensorId: String(state.licensor?.id?.toString() ?? ""),
  //   });
  const {
    data: linePlanningStatusConditions,
    isLoading: isLinePlanningStatusConditionsLoading,
  } = useLinePlanningStatusConditionsContext();

  const buttonDisabled = useMemo(() => {
    return isStateEqualToInitialState(state);
  }, [state]);

  const itemNumberOptions = async (search: string, callback: any) => {
    const response = await fetchTcc({
      search,
      axios,
    });
    callback(getItemNumber(response));
  };


  const globalProductOptions = async (search: string, callback: any) => {
    if (Predicates.isNullOrUndefined(search) || search.length < 3) return [];
    const response = await fetchGlobalProduct({
      category: state.subCategory?.parent?.id ?? state.category?.id,
      sub_category: state.subCategory?.id,
      product_line: state.newGlobalProduct?.id,
      search,
      axios,
    });

    callback(
      getGlobalProduct(response, {
        subCategory: state.subCategory?.id,
        productLine: state.newGlobalProduct?.id,
        id: state.newGlobalProduct?.id,
      }),
    );
  };

  const handleIncludesLeaflet = (item: any) => {
      dispatch(ItemNumberSearchesActions.setIncludesLeafletManual(item));
  };

  const [filteredGPOptions, setFilteredGPOptions] = useState<any[]>([]);
  const [zeroGPOptions, setZeroGPOptions] = useState<any[]>([]);

  useEffect(() => {
  const loadFiltered = async () => {
    const resp = await fetchGlobalProductWithGPCode({
      category: state.subCategory?.parent?.id ?? state.category?.id,
      sub_category: state.subCategory?.id,
      product_line: state.productLine?.id,   
      axios,
    });

    const opts = getGlobalProduct(resp, {
      subCategory: state.subCategory?.id,
      productLine: state.productLine?.id,
      id: state.newGlobalProduct?.id,
    });

    setFilteredGPOptions(opts);
  };

  loadFiltered();
}, [state.category, state.subCategory, state.productLine]);

useEffect(() => {
  const loadZeroFiltered = async () => {
    const resp = await fetchGlobalProductWithoutGPCode({
      category: state.subCategory?.parent?.id ?? state.category?.id,
      sub_category: state.subCategory?.id,
      product_line: state.productLine?.id,
      axios,
    });

    const opts = getGlobalProduct(resp, {
      subCategory: state.subCategory?.id,
      productLine: state.productLine?.id,
      id: state.newGlobalProduct?.id,
    });

    setZeroGPOptions(opts);
  };

  loadZeroFiltered();
}, [state.category, state.subCategory, state.productLine]);

  

  const globalProductOptionsWithGPCode = async (search: string, callback: any) => {
  if (!search || search.length < 3) return [];

  const response = await fetchGlobalProductWithGPCode({
    category: state.subCategory?.parent?.id ?? state.category?.id,
    sub_category: state.subCategory?.id,
    product_line: state.productLine?.id,   
    search,
    axios,
  });

  const options = getGlobalProduct(response, {
    subCategory: state.subCategory?.id,
    productLine: state.productLine?.id,
    id: state.newGlobalProduct?.id,
  });

  callback(options);
};

const globalProductOptionsWithoutGPCode = async (search: string, callback: any) => {
  if (!search || search.length < 3) return [];

  const response = await fetchGlobalProductWithoutGPCode({
    category: state.subCategory?.parent?.id ?? state.category?.id,
    sub_category: state.subCategory?.id,
    product_line: state.productLine?.id,
    search,
    axios,
  });

  const options = getGlobalProduct(response, {
    subCategory: state.subCategory?.id,
    productLine: state.productLine?.id,
    id: state.newGlobalProduct?.id,
  });

  callback(options);
};

const [filteredTccOptions, setFilteredTccOptions] = useState<any[]>([]);

useEffect(() => {
  const loadFilteredTcc = async () => {
    const resp = await fetchTccValid({
      category: state.subCategory?.parent?.id ?? state.category?.id,
      sub_category: state.subCategory?.id,
      product_line: state.productLine?.id,
      global_product: state.newGlobalProduct?.id,
      axios,
    });

    setFilteredTccOptions(getTcc(resp));
  };

  loadFilteredTcc();
}, [state.category, state.subCategory, state.productLine, state.newGlobalProduct]);


const tccOptionsWithPCH = async (search: string, callback: any) => {
  if (!search || search.length < 3) return [];

  const response = await fetchTccValid({
    category: state.subCategory?.parent?.id ?? state.category?.id,
    sub_category: state.subCategory?.id,
    product_line: state.productLine?.id,
    global_product: state.newGlobalProduct?.id,
    search,
    axios,
  });

  callback(getTcc(response));
};


  const tccOptions = async (search: string, callback: any) => {
    if (Predicates.isNullOrUndefined(search) || search.length < 3) return [];
    const response = await fetchItemNumberValid({
      category: state.subCategory?.parent?.id ?? state.category?.id,
      sub_category: state.subCategory?.id,
      product_line: state.newGlobalProduct?.id,
      global_product: state.newGlobalProduct?.id,
      search,
      axios,
    });
    callback(getTcc(response));
  };

  const decoTechOptions = async (search: string, callback: any) => {
    if (Predicates.isNullOrUndefined(search) || search.length < 3) return [];
    const response = await fetchDecorTech({
      search,
      axios,
    });
    callback(getDecorTechs(response));
  };
  
  const handleSubmit = (e: any) => {
    e.preventDefault();

    const dropdownFilters = {
      globalProduct: state.newGlobalProduct,
      category: state.category,
      subCategory: state.subCategory,
      productLine: state.productLine,
      itn_itemno: state.itn_itemno,
      itn_itemnm: state.itn_itemnm,
      marketing_material: state.marketing_material_id,
      itn_cptype: state.itn_cptype,
      itn_cplang: state.itn_cplang,
      itn_cpgcod: state.itn_cpgcod,
      itn_cpgnam: state.itn_cpgnam,
      itn_lifsts: state.itn_lifsts,
      itn_inclfm: state.itn_inclfm,
      itn_lfmnum: state.itn_lfmnum,
      itn_lfmnam: state.itn_lfmnam,
      itn_plgtyp: state.itn_plgtyp,
      itn_plgnam: state.itn_plgnam,
      itn_restri: state.itn_restri,
      itn_ean13d: state.itn_ean13d,
      itn_upc12d: state.itn_upc12d,
      itn_mfpnum: state.itn_mfpnum,
    };

     const filterObject = 
       buildItemNumberSearchFilterObject(state);

    filterProducts(filterObject, dropdownFilters);
  };

  useEffect(() => {
    const dropdownFilters: Partial<ItemNumberSearchesState> =
      buildDropdownFiltersBasedOnLocalStorage(prefix);

    dispatch(
      ItemNumberSearchesActions.setFiltersFromStorage({
        // ...textFilters,
        ...dropdownFilters,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const clearAllFilters = () => {
    dispatch(ItemNumberSearchesActions.resetAllData());
    storeLastFiltersAndResults({}, {}, prefix);
  };

  const formRef = useRef<HTMLFormElement>(null);


  return (
    <>
      <form
        className="gx-3 gy-2"
        onSubmit={handleSubmit}
        id="search-item-number-filter"
        ref={formRef}
      >
        <ExpandableSection
          className="mb-2"
          sectionId="placeholderNewHierarchy"
          title="Item Number Hierarchy"
          expandedByDefault={true}
        >
          <div className="row form-row mb-0">
            {!isSearchRequest && (
              <div className="col-md-6 form-group">
                <label className="form-label">Item Number</label>
                <AsyncSelect
                  name="itemNumber"
                  value={
                    state.itn_itemno
                      ? genericOptionFormatter(
                          state.itn_itemno ||
                            //@ts-ignore
                            getItemNumberDisplayCode(state),
                          state.itn_itemnm,
                          state.itn_itemno,
                        )
                      : null
                  }
                  onChange={(option: any) => {
                    if (option) {
                      dispatch(
                        ItemNumberSearchesActions.setNewItemNumber(
                          `${
                            !!option.value.itn_itemno
                              ? option.value.itn_itemno
                              : option.value.global_product.gpc_lgycod +
                                " " +
                                option.value?.suffix
                          }`,
                          option.value.name,
                        ),
                      );

                      dispatch(
                        ItemNumberSearchesActions.setNewGlobalProduct(
                          option.value?.global_product || null,
                        ),
                      );
                    } else {
                      dispatch(
                        ItemNumberSearchesActions.setNewGlobalProduct(null),
                      );
                      dispatch(
                        ItemNumberSearchesActions.setNewItemNumber("", ""),
                      );
                    }
                  }}
                  defaultOptions={getItemNumber(tccs)}
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      itemNumberOptions,
                      input,
                      getItemNumber(tccs),
                      callback,
                    );
                  }}
                  formatOptionLabel={OptionFormatter}
                  isLoading={isTccsLoading}
                  isClearable={!!state.itn_itemno && !isTccsLoading}
                  isSearchable
                  placeholder="(min 3 digits)"
                  classNamePrefix="react-select"
                />
              </div>
            )}

            <div className="col-md-6 form-group">
              <label className="form-label">Global Product</label>
              <AsyncSelect
                name="globalProduct"
                value={
                  state.newGlobalProduct
                    ? formatGlobalProductIntoOption(state.newGlobalProduct)
                    : null
                }
                onChange={(e) => {
                  dispatch(
                    ItemNumberSearchesActions.setNewGlobalProduct(
                      e?.value ?? null,
                    ),
                  );
                }}
                defaultOptions={filteredGPOptions}
                loadOptions={(input, callback) => {
                  searchDebounce(
                    globalProductOptionsWithGPCode,
                    input,
                    filteredGPOptions,
                    callback,
                  );
                }}
                formatOptionLabel={OptionFormatter}
                isClearable
                isSearchable
                placeholder="(min 3 characters)"
                classNamePrefix="react-select"
              />
            </div>
          </div>
          <div className="row form-row mb-0">
            <div className="col-lg form-group ">
              <label className="form-label">Category</label>
              <Select
                options={getDatacatalog(datacatalog, { type: "Category" })}
                onChange={(e) =>
                  dispatch(
                    ItemNumberSearchesActions.setCategory(e?.value ?? null),
                  )
                }
                formatOptionLabel={OptionFormatter}
                value={
                  state.category
                    ? formatDatacalogIntoOption(state.category)
                    : null
                }
                classNamePrefix="react-select"
                placeholder=""
                isClearable
                isLoading={isDatacatalogLoading}
                components={{
                  IndicatorSeparator: () => null,
                }}
              />
            </div>

            <div className="col-lg form-group ">
              <label className="form-label">Sub-Category</label>
              <Select
                options={getDatacatalog(datacatalog, {
                  type: "Sub-Category",
                  categoryId: state.category?.id,
                })}
                onChange={(e) =>
                  dispatch(
                    ItemNumberSearchesActions.setSubCategory(e?.value ?? null),
                  )
                }
                formatOptionLabel={OptionFormatter}
                value={
                  state.subCategory
                    ? formatDatacalogIntoOption(state.subCategory)
                    : null
                }
                classNamePrefix="react-select"
                placeholder=""
                isClearable
                isLoading={isDatacatalogLoading}
                components={{
                  IndicatorSeparator: () => null,
                }}
              />
            </div>

            <div className="col-lg form-group ">
              <label className="form-label">Product Line</label>
              <Select
                options={getDatacatalog(datacatalog, { type: "Product Line" })}
                onChange={(e) =>
                  dispatch(
                    ItemNumberSearchesActions.setProductLine(e?.value ?? null),
                  )
                }
                value={
                  state.productLine
                    ? formatDatacalogIntoOption(state.productLine)
                    : null
                }
                formatOptionLabel={OptionFormatter}
                classNamePrefix="react-select"
                placeholder=""
                isClearable
                isLoading={isDatacatalogLoading}
                components={{
                  IndicatorSeparator: () => null,
                }}
              />
            </div>
          </div>
        </ExpandableSection>
        <ExpandableSection
          className="mb-2"
          sectionId="itemnumberAttributes"
          title="Item Number Attributes"
          expandedByDefault={true}
        >
          <div className="row form-row mb-0">
            <div className="col-md-3 form-group">
              <label className="form-label">Consumer Packaging Type</label>
              <Select
                onChange={(e) =>
                  dispatch(
                    ItemNumberSearchesActions.setConsumerPackagingType(
                      e?.value ?? null,
                    ),
                  )
                }
                formatOptionLabel={OptionFormatter}
                value={formatConsumerTypeIntoOption(state.itn_cptype)}
                options={getConsumerTypes(consumerTypes)}
                classNamePrefix="react-select"
                placeholder=""
                isClearable
                isLoading={isConsumerTypesLoading}
                components={{
                  IndicatorSeparator: () => null,
                }}
              />
            </div>

            <div className="col-md-3 form-group">
              <label className="form-label">Consumer Packaging Language</label>
              <input
                onChange={(e) =>
                  dispatch(
                    ItemNumberSearchesActions.setConsumerPackagingLanguage(
                      e.target.value,
                    ),
                  )
                }
                value={state.itn_cplang}
                type="text"
                className="form-control d-inline"
              />
            </div>
            <div className="col-md-3 form-group">
              <label className="form-label">
                Consumer Packaging Global Code
              </label>
              <input
                onChange={(e) =>
                  dispatch(
                    ItemNumberSearchesActions.setConsumerPackagingGlobalCode(
                      e.target.value,
                    ),
                  )
                }
                value={state.itn_cpgcod}
                type="text"
                className="form-control d-inline"
              />
            </div>

            <div className="col-lg form-group">
              <label className="form-label">
                Consumer Packaging Global Name
              </label>
              <div className="d-flex align-items-center">
                <input
                  onChange={(e) =>
                    dispatch(
                      ItemNumberSearchesActions.setConsumerPackagingGlobalName(
                        e.target.value,
                      ),
                    )
                  }
                  value={state.itn_cpgnam}
                  type="text"
                  className="form-control flex-grow-1"
                />
              </div>
            </div>

            <div className="col-md-3 form-group">
              <label className="form-label">Includes Leaflet or Manual</label>
              <Select
                onChange={handleIncludesLeaflet}
                formatOptionLabel={OptionFormatter}
                value={state.itn_inclfm}
                options={yesOrNoOptions}
                classNamePrefix="react-select"
                placeholder=""
                isClearable
                components={{
                  IndicatorSeparator: () => null,
                }}
              />
            </div>

            <div className="col-md-3 form-group">
              <label className="form-label">Leaflet or Manual Number</label>
              <input
                onChange={(e) =>
                  dispatch(
                    ItemNumberSearchesActions.setIncludesLeafletNumber(
                      e.target.value,
                    ),
                  )
                }
                value={state.itn_lfmnum}
                type="text"
                className="form-control d-inline"
              />
            </div>

            <div className="col-md-3 form-group">
              <label className="form-label">Leaflet or Manual Name</label>
              <input
                onChange={(e) =>
                  dispatch(
                    ItemNumberSearchesActions.setIncludesLeafletName(
                      e.target.value,
                    ),
                  )
                }
                value={state.itn_lfmnam}
                type="text"
                className="form-control d-inline"
              />
            </div>

            <div className="col-md-3 form-group">
              <label className="form-label">Material (Marketing) </label>
              <Select
                onChange={(e) =>{
                  dispatch(
                    ItemNumberSearchesActions.setMarketingMaterial(
                      e?.value ?? null,
                    ),
                  );
                }
                }
                formatOptionLabel={OptionFormatter}
                value={formatMarketingMaterialIntoOption(state.marketing_material_id)}
                options={getMarketingMaterials(marketingMaterials)}
                classNamePrefix="react-select"
                placeholder=""
                isClearable
                isLoading={isMarketingMaterialsLoading}
                components={{
                  IndicatorSeparator: () => null,
                }}
              />
            </div>
          </div>

          <div className="row form-row mb-0">
            <div className="col-md-3 form-group">
              <label className="form-label">Plug Type</label>
              <Select
                onChange={(e) =>
                  dispatch(
                    ItemNumberSearchesActions.setPlugType(e?.value ?? null),
                  )
                }
                formatOptionLabel={OptionFormatter}
                value={formatPlugTypeIntoOption(state.itn_plgtyp)}
                options={getPlugTypes(plugTypes)}
                classNamePrefix="react-select"
                placeholder=""
                isClearable
                isLoading={isPlugTypesLoading}
                components={{
                  IndicatorSeparator: () => null,
                }}
              />
            </div>
            <div className="col-md-3 form-group">
              <label className="form-label">Plug Type Name</label>
              <input
                onChange={(e) =>
                  dispatch(
                    ItemNumberSearchesActions.setPlugName(e.target.value),
                  )
                }
                value={state.itn_plgnam}
                type="text"
                className="form-control d-inline"
              />
            </div>

            {!isSearchRequest && (
              <div className="col-md-3 form-group">
                <Tooltip
                  id="global-msrp"
                  className="tooltip-text-box"
                  place="top-start"
                  noArrow={true}
                />
                <label className="form-label">UPC 12 Digits US</label>
                <input
                  onChange={(e) =>
                    dispatch(ItemNumberSearchesActions.setUPC12(e.target.value))
                  }
                  value={state.itn_upc12d}
                  type="text"
                  className="form-control d-inline"
                />
              </div>
            )}
            {!isSearchRequest && (
              <div className="col-md-3 form-group">
                <Tooltip
                  id="global-msrp"
                  className="tooltip-text-box"
                  place="top-start"
                  noArrow={true}
                />
                <label className="form-label">EAN 13 Digits</label>
                <input
                  onChange={(e) =>
                    dispatch(
                      ItemNumberSearchesActions.setEAN13D(e.target.value),
                    )
                  }
                  value={state.itn_ean13d}
                  type="text"
                  className="form-control d-inline"
                />
              </div>
            )}
          </div>

          {!isSearchRequest && (
            <div className="row form-row mb-0">
              <div className="col-md-3 form-group">
                <label className="form-label">Regulatory Restrictions</label>
                <input
                  onChange={(e) =>
                    dispatch(
                      ItemNumberSearchesActions.setRegulatoryRestrictions(
                        e.target.value,
                      ),
                    )
                  }
                  value={state.itn_restri}
                  type="text"
                  className="form-control d-inline"
                />
              </div>
              <div className="col-md-3 form-group">
                <Tooltip
                  id="global-msrp"
                  className="tooltip-text-box"
                  place="top-start"
                  noArrow={true}
                />
                <label className="form-label">Release Date</label>
                <input
                  onChange={(e) =>
                    dispatch(
                      ItemNumberSearchesActions.setReleaseDate(e.target.value),
                    )
                  }
                  value={state.itn_relsdt}
                  type="date"
                  min="1993-01-01"
                  className="form-control flex-grow-1"
                />
              </div>
              <div className="col-md-3 form-group">
                <label className="form-label">{LIFECYCLE_STATUS_LABEL}</label>
                <Select
                  onChange={(e) =>
                    dispatch(ItemNumberSearchesActions.setLinePlanningStatus(e))
                  }
                  formatOptionLabel={OptionFormatter}
                  value={state.itn_lifsts}
                  options={getLinePlanningStatusOptions(
                    linePlanningStatusConditions,
                  )}
                  classNamePrefix="react-select"
                  placeholder=""
                  isClearable
                  isLoading={isLinePlanningStatusConditionsLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                />
              </div>
            </div>
          )}
        </ExpandableSection>

        <div className="d-flex gap-3 form-group align-items-end justify-content-end mt-2">
          <input
            disabled={buttonDisabled}
            type="button"
            onClick={clearAllFilters}
            className="btn btn-outline-primary"
            value="Clear Filters"
          />

          <input
            disabled={buttonDisabled}
            type="submit"
            className="btn btn-primary"
            value="Search"
          />
        </div>
      </form>
    </>
  );
};
export default SearchFilters;



